import type { ReadingSlide } from "../ui/StartReader";
import { AppChip, C, Frame, MONO } from "../ui/readingArt";

/**
 * "Read while you wait" summary for the pause-and-verify start screen. Three
 * short slides (what/who and why a human is the control, what to watch for,
 * key terms) shown in the reader while the 3D room downloads. Content only;
 * the reader UI lives in ui/StartReader.tsx.
 */

/* ------------------------------------------------------------------ */
/* 1. a visitor asks to be let in, then pause / verify / decide        */
/* ------------------------------------------------------------------ */
function StepRow({ y, n, title, sub, ring }: { y: number; n: string; title: string; sub: string; ring?: boolean }): JSX.Element {
  return (
    <g>
      <rect x="16.5" y={y + 0.5} width="327" height="37" rx="8" fill={C.panel2} stroke={C.line} />
      {ring ? <circle className="sr-anim-ring" cx="38" cy={y + 19} r="10" fill="none" stroke={C.mint} strokeWidth="1.5" /> : null}
      <circle cx="38" cy={y + 19} r="10" fill={C.mint} fillOpacity="0.14" />
      <text x="38" y={y + 23} fontSize="11.5" fontWeight="700" fill={C.mint} textAnchor="middle">
        {n}
      </text>
      <text x="58" y={y + 17} fontSize="12.5" fontWeight="700" fill={C.text}>
        {title}
      </text>
      <text x="58" y={y + 31} fontSize="10.5" fill={C.muted}>
        {sub}
      </text>
    </g>
  );
}

function DoorArt(): JSX.Element {
  const H = 296;
  return (
    <Frame label="Example: a request at the door" aria="A visitor asks to be let in, followed by three steps: pause, verify, then decide" height={H}>
      <AppChip x={16} y={36} />
      <text x="50" y="53" fontSize="14" fontWeight="700" fill={C.text}>
        Reception
      </text>
      <rect className="sr-anim-pulse" x="250" y="37" width="94" height="24" rx="12" fill={C.mint} fillOpacity="0.14" stroke={C.mint} strokeOpacity="0.45" />
      <text x="297" y="53.5" fontSize="11" fontWeight="700" fill={C.mint} letterSpacing="0.6" textAnchor="middle">
        VERIFY
      </text>
      <line x1="16" y1="74.5" x2="344" y2="74.5" stroke={C.line} />

      <g className="sr-anim-float">
        <circle cx="32" cy="108" r="13" fill={C.panel2} stroke={C.line} />
        <circle cx="32" cy="104" r="4.2" fill="none" stroke={C.muted} strokeWidth="1.5" />
        <path d="M24.5 115 a7.5 6 0 0 1 15 0" fill="none" stroke={C.muted} strokeWidth="1.5" strokeLinecap="round" />
      </g>
      <text x="56" y="92" fontSize="10.5" fill={C.muted}>
        Visitor at the secured door
      </text>
      <rect x="56.5" y="98.5" width="287" height="34" rx="9" fill={C.panel2} stroke={C.line} />
      <text x="69" y="120" fontSize="12.5" fill={C.text}>
        {"Sorry, I\u2019m late. Can you let me in?"}
      </text>

      <StepRow y={148} n="1" title="Pause" sub="Do not act on the first request" ring />
      <StepRow y={192} n="2" title="Verify" sub="Use a route you already trust" />
      <StepRow y={236} n="3" title="Then decide" sub="Allow, refuse or hand it on" />
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* 2. a convincing executive on a video call asks for a payment        */
/* ------------------------------------------------------------------ */
function Chip({ x, label, tone }: { x: number; label: string; tone: "mint" | "red" }): JSX.Element {
  const col = tone === "red" ? C.red : C.mint;
  return (
    <g>
      <rect x={x + 0.5} y="250.5" width="99" height="30" rx="15" fill={col} fillOpacity="0.1" stroke={col} strokeOpacity="0.45" />
      <text x={x + 50} y="269.5" fontSize="11.5" fontWeight="700" fill={col} textAnchor="middle">
        {label}
      </text>
    </g>
  );
}

function CallArt(): JSX.Element {
  const H = 300;
  return (
    <Frame label="Example: video call" aria="A video call where an executive asks for an urgent payment; the face and voice look right but are not proof" height={H}>
      <AppChip x={16} y={36} />
      <text x="50" y="53" fontSize="14" fontWeight="700" fill={C.text}>
        Video call
      </text>
      <circle className="sr-anim-blink" cx="296" cy="49" r="4" fill={C.red} />
      <text x="306" y="53" fontSize="11" fontWeight="700" fill={C.muted} letterSpacing="0.6">
        LIVE
      </text>
      <line x1="16" y1="74.5" x2="344" y2="74.5" stroke={C.line} />

      <rect x="16.5" y="84.5" width="327" height="118" rx="9" fill={C.code} stroke={C.line} />
      <circle className="sr-anim-ring" cx="180" cy="124" r="22" fill="none" stroke={C.amber} strokeWidth="1.5" />
      <g className="sr-anim-float">
        <circle cx="180" cy="124" r="22" fill={C.panel2} stroke={C.line} />
        <circle cx="180" cy="118" r="7.5" fill="none" stroke={C.soft} strokeWidth="1.6" />
        <path d="M166 138 a14 11 0 0 1 28 0" fill="none" stroke={C.soft} strokeWidth="1.6" strokeLinecap="round" />
      </g>
      <path d="M140 202 C140 168 220 168 220 202 Z" fill={C.panel2} stroke={C.line} />
      <rect x="26" y="172" width="104" height="22" rx="6" fill={C.panel} stroke={C.line} />
      <text x="36" y="187" fontSize="10.5" fontFamily={MONO} fill={C.soft}>
        Chief Executive
      </text>

      <rect x="16.5" y="212.5" width="327" height="28" rx="8" fill={C.amber} fillOpacity="0.1" stroke={C.amber} strokeOpacity="0.4" />
      <text x="30" y="230.5" fontSize="12" fontWeight="600" fill={C.amber}>
        Pay this today. Keep it between us.
      </text>

      <Chip x={16} label="Looks right" tone="mint" />
      <Chip x={130} label="Sounds right" tone="mint" />
      <Chip x={244} label="Not proof" tone="red" />
    </Frame>
  );
}

/* ------------------------------------------------------------------ */
/* 3. terms: an incoming request vs. a route you already trust         */
/* ------------------------------------------------------------------ */
function RouteArt(): JSX.Element {
  const H = 296;
  return (
    <Frame label="Verify through a trusted route" aria="An incoming call is not proof; calling back on a number you already had is" height={H}>
      <AppChip x={16} y={36} />
      <text x="50" y="53" fontSize="14" fontWeight="700" fill={C.text}>
        Check the route
      </text>
      <line x1="16" y1="74.5" x2="344" y2="74.5" stroke={C.line} />

      <rect x="16.5" y="86.5" width="154" height="86" rx="10" fill={C.red} fillOpacity="0.07" stroke={C.red} strokeOpacity="0.45" strokeDasharray="4 3" />
      <path d="M30 105 q0 -4 3.5 -4 l3 0 l1.6 4.2 l-2.2 1.6 q1.6 3.4 4.8 5 l1.6 -2.2 l4.2 1.6 l0 3 q0 3.5 -4 3.5 q-12.5 -1 -12.5 -12.7 z" fill="none" stroke={C.red} strokeWidth="1.4" strokeLinejoin="round" />
      <text x="52" y="110" fontSize="12" fontWeight="700" fill={C.text}>
        Incoming call
      </text>
      <text x="30" y="132" fontSize="11" fill={C.soft}>
        {"\u201CI\u2019m from the bank\u201D"}
      </text>
      <text x="30" y="152" fontSize="10.5" fill={C.red}>
        Caller ID can be faked
      </text>

      <rect x="190.5" y="86.5" width="154" height="86" rx="10" fill={C.mint} fillOpacity="0.07" stroke={C.mint} strokeOpacity="0.45" />
      <circle cx="205" cy="105" r="8" fill={C.mint} fillOpacity="0.16" />
      <polyline points="201.5,105 204,107.6 208.5,102.2" fill="none" stroke={C.mint} strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" />
      <text x="220" y="110" fontSize="12" fontWeight="700" fill={C.text}>
        Your contacts
      </text>
      <text x="204" y="132" fontSize="11" fill={C.soft}>
        Number you already had
      </text>
      <text x="204" y="152" fontSize="10.5" fill={C.mint}>
        Official site or desk
      </text>

      <path d="M93 173 V200 H128" fill="none" stroke={C.mint} strokeWidth="1.5" strokeDasharray="4 3" strokeLinecap="round" />
      <path d="M232 200 H267 V177" fill="none" stroke={C.mint} strokeWidth="1.5" strokeDasharray="4 3" strokeLinecap="round" />
      <polyline points="262,183 267,176 272,183" fill="none" stroke={C.mint} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
      <rect className="sr-anim-pulse" x="130" y="188" width="100" height="24" rx="12" fill={C.mint} fillOpacity="0.14" stroke={C.mint} strokeOpacity="0.45" />
      <text x="180" y="204" fontSize="11" fontWeight="700" fill={C.mint} textAnchor="middle">
        Call back
      </text>

      <circle cx="26" cy="241" r="4.5" fill={C.red} />
      <text x="40" y="245" fontSize="12" fill={C.text}>
        They reached you: not proof
      </text>
      <circle cx="26" cy="266" r="4.5" fill={C.mint} />
      <text x="40" y="270" fontSize="12" fill={C.text}>
        You reached them: proof
      </text>
    </Frame>
  );
}

export const PAUSE_AND_VERIFY_READING: ReadingSlide[] = [
  {
    tab: "Why it matters",
    headline: "Plausible is not proven.",
    illustration: DoorArt,
    caption: "Pause first. Then verify.",
    sections: [
      {
        h: "What this is about",
        body: "A normal day at the office, where someone tries to talk you into a decision: let them in, join a call, plug in a device or read out a code. You watch a short film, then practise scenes.",
      },
      {
        h: "Who it\u2019s for",
        body: "Anyone who works in an office, answers calls or has a work login. No technical skills needed.",
      },
      {
        h: "Why your judgement matters",
        body: "A convincing person, call or page is not proof. Locks, filters and policies only help if someone follows the process. Pausing to verify is a human call. Yours.",
      },
    ],
  },
  {
    tab: "What to watch for",
    headline: "The story is built to stop you checking.",
    illustration: CallArt,
    caption: "A face and a voice can be copied.",
    sections: [
      {
        h: "Pressure to skip the process",
        body: "Urgency, authority and a friendly story are the tools. Their job is to make checking feel rude or slow.",
      },
      {
        h: "Faces and voices can be faked",
        body: "A video call or a phone voice can be a deepfake. Seeing and hearing someone is not enough to approve a payment.",
      },
      {
        h: "Things you did not ask for",
        body: "An unknown Wi-Fi, a stray USB device, a code on a sticker. Each wants you to connect, plug in or scan.",
      },
      {
        h: "A login or a code on request",
        body: "No real caller needs you to read out a code or a work password. Hang up and call back on a number you already had.",
      },
    ],
  },
  {
    tab: "Key terms",
    headline: "The words you\u2019ll meet today.",
    illustration: RouteArt,
    caption: "Short definitions \u2014 refer back any time.",
    sections: [
      { h: "Social engineering", body: "Talking a person into acting, instead of breaking into a system." },
      { h: "Pretext", body: "A believable story built to make a request seem normal." },
      { h: "Verify", body: "Check through a route you already trust, not the one the request arrived on." },
      { h: "Deepfake", body: "Video or voice faked to look and sound like a real person." },
      { h: "Rogue Wi-Fi", body: "A network that copies a trusted name to sit between you and the real one." },
      { h: "Voice phishing", body: "Phishing by phone call, often with details that make the caller sound real." },
      { h: "QR code phishing", body: "A code that sends you to a fake page built to take your login." },
    ],
  },
];
